import { differenceInSeconds, format } from "date-fns";
import { parseISO } from "date-fns/esm";

export const cvToVw = (px: number) => {
  const OFFSET = 1920;

  return (px / OFFSET) * 100;
};

// 텍스트를 클립보드에 복사하는 함수
export const copyToClipboard = (text: string) => {
  /**
   * text : 클립보드에 복사할 텍스트
   * ※ 함수 사용 후 아래 코드 실행해야 토스트 메시지 출력됨.
   * setToastOpen((prev) => {
      return {
        ...prev,
        copySuccess: true,
      };
    });
   */
  navigator.clipboard.writeText(text);
};

/**
 * 시간차 구하는 함수
 *
 * @param startTime : 시간차 시작 시간
 * @param endTime : 시간차 끝 시간
 * @param text : 리턴되는 데이터에 넣을 문자열
 *
 * 시간은 데이트 형식으로 포멧하여 전달되어야 함.
 */
export const timeCalculator = (
  startTime: Date,
  endTime: Date,
  text?: string
) => {
  let diff = differenceInSeconds(new Date(endTime), new Date(startTime));
  // 초단위
  if (diff < 60) {
    return `${diff}초${text ? text : ""}`;
  }
  // 분단위
  else if (diff < 3600) {
    if (diff % 60 === 0) {
      return `${Math.floor(diff / 60)}분${text ? text : ""}`;
    } else {
      return `${Math.floor(diff / 60)}분 ${diff % 60}초${
        text ? text : ""
      }`;
    }
  }
  // 시간단위
  else if (diff >= 3600) {
    if (diff % 3600 === 0) {
      return `${Math.floor(diff / 3600)}시간${text ? text : ""}`;
    } else if (diff % 60 === 0) {
      return `${Math.floor(diff / 3600)}시간 ${Math.floor(
        (diff % 3600) / 60
      )}분${text ? text : ""}`;
    } else {
      return `${Math.floor(diff / 3600)}시간 ${Math.floor(
        (diff % 3600) / 60
      )}분 ${diff % 60}초${text ? text : ""}`;
    }
  }
};

// 비밀번호 유효성 검사하는 함수 (8자리 이상, 숫자, 영문, 특문 포함)
export const isValidPassword = (password: string) => {
  if (password.length > 0) {
    const regex =
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?-]).{8,}$/;
    return regex.test(password);
  } else return true;
};

// 숫자만 입력했는지 검사하는 함수
export const isOnlyNumber = (text: string) => {
  if (text.length > 0) {
    const regex = /^[0-9]+$/;
    return regex.test(text);
  } else return true;
};

/**
 * 정렬 함수
 *
 * @param filter: 클릭된 테이블 헤더의 id
 * @param setSort: api에 전달할 파라미터를 관리할 setState함수
 *
 * 리턴된 데이터가 파라미터 값이 됨
 */
export const handleClickForSort = (filter: string, setSort: any) => {
  setSort((prev: any) => {
    // 클릭시 기본 정렬은 내림 차순
    if (!prev || !prev.includes(filter)) {
      return `${filter} ASC`;
    } else if (prev === `${filter} ASC`) {
      return `${filter} DESC`;
    } else if (prev && prev === `${filter} DESC`) {
      return "";
    }
  });
};

/**
 * 긴 문자열 자르는 함수
 *
 * @param text : substring할 text. undefined | null인 경우 ""리턴
 * @param start : substring을 시작할 index
 * @param end : substring을 끝낼 index
 * @param screenSize? : 전달시 window.innerWidth가 전달값보다 작은 경우 substring 하지 않음 (모바일에서 텍스트 잘림 방지)
 * @returns substring된 text
 */
export const textSubstring = (
  text: string | undefined | null,
  start: number,
  end: number,
  screenSize?: number
): string => {
  let result = "";
  if (!text) return result;
  if (screenSize) {
    if (window.innerWidth <= screenSize) {
      return (result = text);
    }
  }

  if (text.length > end) {
    result = text.substring(start, end) + "...";
  } else {
    result = text;
  }
  return result;
};

/**
 * 누적거리를 전달받아서 경로에 찍힐 화살표 간격을 계산해주는 함수 (지도 폴리라인용)
 *
 * @param distance: 누적 거리
 * @param setState: 화살표 간격 상태 관리 함수
 */
export const calculateArrowInterval = (
  distance: number,
  setState: React.Dispatch<React.SetStateAction<number>>
) => {
  if (distance === 0) {
    setState(10);
  } else if (distance <= 5 && distance > 0) {
    setState(Math.round(distance / 0.02));
  } else if (distance <= 15 && distance > 5) {
    setState(Math.round(distance / 0.04));
  } else if (distance <= 30 && distance > 15) {
    setState(Math.round(distance / 0.06));
  } else if (distance <= 60 && distance > 30) {
    setState(Math.round(distance / 0.1));
  } else if (distance > 60) {
    setState(700);
  }
};

/**
 * 경로 반경 반지름을 전달받아서 적절한 지도 레벨을 세팅해주는 함수
 *
 * @param radius: 경로데이터의 반경 반지름값
 * @param setMapLevel: 지도 레벨 상태 관리 함수
 */
export const checkRouteRadius = (
  radius: number,
  setMapLevel: React.Dispatch<React.SetStateAction<number>>
) => {
  if (radius == null || radius < 0.1) {
    return setMapLevel(1);
  } else if (radius < 0.35) {
    return setMapLevel(2);
  } else if (radius < 0.5) {
    return setMapLevel(3);
  } else if (radius < 1.2) {
    return setMapLevel(4);
  } else if (radius < 2) {
    return setMapLevel(5);
    // } else if (radius < 5) {
  } else if (radius < 4.5) {
    return setMapLevel(6);
  } else if (radius < 9) {
    return setMapLevel(7);
  } else if (radius < 15) {
    return setMapLevel(8);
  } else if (radius < 35) {
    return setMapLevel(9);
  } else if (radius < 80) {
    return setMapLevel(10);
  } else if (radius < 150) {
    return setMapLevel(11);
  } else if (radius >= 150) {
    return setMapLevel(12);
  }
};

// 비밀번호 형식 및 일치 확인
export const checkPassword = ({
  pw,
  pwCheck,
}: {
  pw?: string;
  pwCheck?: string;
}) => {
  let result = {
    pwError: false,
    pwErrorReason: "",
    pwCheckError: false,
    pwCheckErrorReason: "",
  };
  if (!pw && !pwCheck) return result;
  if (pw) {
    if (pw.length < 8 || !isValidPassword(pw)) {
      result.pwError = true;
      result.pwErrorReason = `영문, 숫자, 특수문자를 최소 1개씩 포함하고, 총 8자리 이상이어야 합니다.`;
    } else {
      result.pwError = false;
      result.pwErrorReason = "";
    }
    if (pw.includes(" ")) {
      result.pwError = true;
      result.pwErrorReason = `비밀번호에 공백은 사용할 수 없습니다.`;
    }
    if (!pwCheck) {
      result.pwCheckError = true;
      result.pwCheckErrorReason = "비밀번호가 일치하지 않습니다.";
    }
    if (pw === pwCheck) {
      result.pwCheckError = false;
      result.pwCheckErrorReason = "";
    } else {
      result.pwCheckError = true;
      result.pwCheckErrorReason = "비밀번호가 일치하지 않습니다.";
    }
  }

  if (!pw) {
    if (pwCheck) {
      result.pwCheckError = true;
      result.pwCheckErrorReason = "비밀번호가 일치하지 않습니다.";
    }
  }

  return result;
};

// 이메일 @, . 확인
export const isValidEmail = (str?: string): boolean => {
  // 문자열이 주어지지 않은 경우 true 반환
  if (!str || str === "") return true;

  // 이메일 형식인 경우 true를 반환
  if (/^\w+@[a-zA-Z_]+?\.[a-zA-Z]{2,3}$/.test(str)) return true;
  // 그 외의 경우에는 false를 반환
  return false;
};

// 전화 번호 3자리-3/4자리-4자리 확인
export const isValidPhone = (num?: string) => {
  // 문자열이 주어지지 않은 경우 또는 빈 문자열인 경우 true를 반환
  if (!num || num === "") return true;

  // 숫자 3자리-숫자 3자리 또는 4자리-숫자 4자리 형식인 경우 true를 반환
  if (/^\d{2,3}-\d{3,4}-\d{4}$/.test(num)) return true;

  // 그 외의 경우에는 false를 반환
  return false;
};

export const isMobile = () => {
  const userAgent = navigator.userAgent;

  // 모바일 기기의 userAgent를 감지
  if (/android/i.test(userAgent)) {
    return true;
  }

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return true;
  }

  // 그 외의 경우 데스크탑으로 판단
  return false;
};

/**
 * 연락처 전달받아서 가운데자리 마스킹해주는 함수
 *
 * 전달된 값이 없거나 연락처 형식이 아니면 빈 값을 리턴
 */
export const maskContactNumber = (input?: string): string => {
  if (!input) return ""; // 전달된 값이 없으면 빈 문자열 리턴

  const pattern = /^(\d{3})-(\d{3,4})-(\d{4})$/; // 숫자3개-숫자3~4개-숫자4개 패턴
  const match = input.match(pattern);

  if (!match) return ""; // 형식이 맞지 않으면 빈 문자열 리턴

  const [_, first, middle, last] = match; // 각 부분을 추출
  const maskedMiddle = "*".repeat(middle.length); // 가운데 자리 마스킹 처리

  return `${first}-${maskedMiddle}-${last}`;
};

export const formatDate = (
  dateString: string,
  formatString: string = "yyyy/MM/dd"
) => {
  const date = parseISO(dateString);
  return format(date, formatString);
};

// 암호학적으로 안전한 32비트 정수 난수 생성
export const randomInt = () => {
  const array = new Uint32Array(1); // 32비트 정수 배열 생성
  crypto.getRandomValues(array); // 배열에 암호학적 난수 채우기
  return array[0]; // 생성된 난수 반환
};

export const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault(); // 기본 동작 방지 (예: Space 키의 스크롤)
    e.currentTarget.click(); // 클릭 이벤트 트리거
  }
};

/**
 * 날짜데이터를 전달받아 현재 시간과 비교해서 24시간보다 오래됐으면 true 아니면 false를 리턴 2023-05-02 15:11:45
 */
export function isOlderThan24Hours(gpsDate: string): boolean {
  if (!gpsDate) return false;
  const parsed = new Date(gpsDate.replace(" ", "T"));
  if (isNaN(parsed.getTime())) return false; // 날짜 파싱 실패 시 false

  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  return diffHours >= 24;
}

/**
 * 연락처 가운데 자리를 마스킹하는 함수
 * @param phoneNumber 마스킹할 전화번호 (형식: 숫자-숫자-숫자)
 * @returns 가운데 숫자가 *로 마스킹된 전화번호 문자열 (잘못된 형식이면 빈 문자열 반환)
 */
export const maskPhoneNumber = (phoneNumber: string): string => {
  const parts = phoneNumber.split("-");

  if (parts.length !== 3) {
    return "";
  }

  const [first, middle, last] = parts;

  if (!first || !middle || !last) {
    return "";
  }

  const maskedMiddle = "*".repeat(middle.length);

  return `${first}-${maskedMiddle}-${last}`;
};
